import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { validateWorkspaceForCreate } from '@/lib/workspaceValidation';
import { LeadsService } from '@/data/leads.service';
import { getDefaultBusinessUnitId } from '@/lib/businessUnitHelper';
import { useAuth } from '@/lib/auth';
import { TablesInsert } from '@/integrations/supabase/types';
import { toast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import { handleError } from '@/lib/errorHandler';
import { completeLead } from '@/lib/leads/actions';
import type { ActionType, ActionOutcome } from './useActionEvents';

// Helper to log action events (gracefully handles missing table)
async function logActionEvent(
  workspaceId: string,
  userId: string,
  leadId: string,
  actionType: ActionType,
  outcome?: ActionOutcome
) {
  try {
    // Check if table exists first
    const testQuery = await supabase
      .from('action_events')
      .select('id')
      .limit(1);
    
    if (testQuery.error && (testQuery.error.code === '42P01' || testQuery.error.message?.includes('does not exist'))) {
      logger.warn('[logActionEvent] action_events table does not exist, skipping log', { leadId, workspaceId });
      return;
    }

    const { error } = await supabase
      .from('action_events')
      .insert([{
        workspace_id: workspaceId,
        user_id: userId,
        lead_id: leadId,
        action_type: actionType,
        outcome: outcome || null,
      }]);

    if (error) {
      // If it's a table not found error, just log and return
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        logger.warn('[logActionEvent] action_events table does not exist, skipping log', { leadId, workspaceId });
        return;
      }
      throw error;
    }
  } catch (error: any) {
    // If action_events table doesn't exist, log warning but don't fail
    if (error?.code === '42P01' || error?.message?.includes('does not exist')) {
      logger.warn('[logActionEvent] action_events table does not exist, skipping log', { leadId, workspaceId });
      return;
    }
    throw error;
  }
}

export function useCreateLead() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId, mode } = useWorkspaceScope();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (lead: Omit<TablesInsert<'leads'>, 'workspace_id' | 'business_unit_id'>) => {
      // Validate workspace before creating lead
      const validation = validateWorkspaceForCreate(activeWorkspaceId, mode, 'create a lead');
      if (!validation.isValid) {
        throw new Error(validation.errorMessage || 'No workspace available. Please select a workspace.');
      }
      
      const workspaceId = validation.workspaceId!;

      // Get default business_unit_id for the workspace
      const businessUnitId = await getDefaultBusinessUnitId(workspaceId);

      logger.mutation('createLead', { workspaceId, userId: user?.id });
      return LeadsService.createLead({
        workspace_id: workspaceId,
        business_unit_id: businessUnitId,
        ...lead,
      });
    },
    onSuccess: () => {
      // CRITICAL: Invalidate all leads queries (exact: false to match all workspace scopes)
      queryClient.invalidateQueries({ queryKey: ['leads'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['deals'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['elite-today-plan'], exact: false });
      toast({ title: 'Lead created', description: 'New lead has been added successfully.' });
    },
    onError: (error) => {
      handleError(error, { action: 'Create Lead', component: 'useCreateLead', workspaceId: activeWorkspaceId || undefined });
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!activeWorkspaceId) throw new Error('No workspace available');
      await LeadsService.deleteLead(id, activeWorkspaceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({ title: 'Lead deleted', description: 'Lead has been removed.' });
    },
    onError: (error) => {
      handleError(error, { action: 'Delete Lead', component: 'useDeleteLead', workspaceId: activeWorkspaceId });
    },
  });
}

export function useBulkCreateLeads() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId, mode } = useWorkspaceScope();

  return useMutation({
    mutationFn: async (leads: Omit<TablesInsert<'leads'>, 'workspace_id'>[]) => {
      logger.mutation('bulkCreateLeads', { leadsCount: leads.length });

      // CRITICAL: Ensure authentication is present before import
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        const errorMsg = 'Not authenticated. Please log in to import leads.';
        logger.error('[useBulkCreateLeads] Authentication error', authError, { leadsCount: leads.length });
        toast({
          title: 'Not authenticated',
          description: 'Please log in to import leads.',
          variant: 'destructive'
        });
        throw new Error(errorMsg);
      }

      // CRITICAL: Validate workspace before import
      const validation = validateWorkspaceForCreate(activeWorkspaceId, mode, 'import leads');
      if (!validation.isValid) {
        logger.error('[useBulkCreateLeads] Workspace validation failed', { userId: user.id, mode, activeWorkspaceId });
        throw new Error(validation.errorMessage || 'No workspace available. Please select a workspace before importing leads.');
      }
      
      const workspaceId = validation.workspaceId!;

      // Get default business_unit_id for the workspace
      const businessUnitId = await getDefaultBusinessUnitId(workspaceId);

      // Build leadsToInsert with proper types and safe defaults
      // CRITICAL: Always include workspace_id (NOT NULL constraint)
      const leadsToInsert: TablesInsert<'leads'>[] = leads.map((lead) => {
        const cleanLead: TablesInsert<'leads'> = {
          name: lead.name || 'Unknown', // Required NOT NULL
          workspace_id: workspaceId, // ALWAYS include - NOT NULL constraint prevents recursion
          business_unit_id: businessUnitId, // Default business unit for workspace
          // Optional fields - only include if not null/undefined
          ...(lead.email && { email: lead.email }),
          ...(lead.phone && { phone: lead.phone }),
          ...(lead.company && { company: lead.company }),
          ...(lead.notes_short && { notes_short: lead.notes_short }),
          ...(lead.notes_long && { notes_long: lead.notes_long }),
          // Enums with defaults
          source: lead.source || 'manual',
          temperature: lead.temperature || 'hot', // New leads start as HOT
          pipeline_stage: lead.pipeline_stage || 'new',
          // Numeric defaults
          decision_score: lead.decision_score ?? 0,
          // Array defaults - ensure tags is always string[]
          tags: Array.isArray(lead.tags) ? lead.tags : (lead.tags ? [lead.tags] : []),
        };

        return cleanLead;
      });

      // Insert in chunks to avoid payload limits and stack overflow
      // Using 50 leads per batch as a balance between speed and reliability
      const CHUNK_SIZE = 50;
      const chunks: TablesInsert<'leads'>[][] = [];
      for (let i = 0; i < leadsToInsert.length; i += CHUNK_SIZE) {
        chunks.push(leadsToInsert.slice(i, i + CHUNK_SIZE));
      }

      logger.action('bulkImportStart', { totalLeads: leadsToInsert.length, chunks: chunks.length, workspaceId });

      let successCount = 0;
      const errors: Array<{ chunk: number; error: any }> = [];

      // Use RPC function to bypass RLS and prevent stack overflow
      // Convert chunks to JSONB format for RPC
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        logger.action('bulkImportChunk', { chunk: i + 1, totalChunks: chunks.length, leadsInChunk: chunk.length });

        // Convert chunk to JSONB array (only columns that exist in production)
        const chunkJson = chunk.map(lead => ({
          name: lead.name,
          email: lead.email || null,
          phone: lead.phone || null,
          company: lead.company || null,
          notes_short: lead.notes_short || null,
          notes_long: lead.notes_long || null,
          source: lead.source || 'manual',
          temperature: lead.temperature || 'hot', // New leads start as HOT
          pipeline_stage: lead.pipeline_stage || 'new',
          decision_score: lead.decision_score ?? 0,
          tags: Array.isArray(lead.tags) ? lead.tags : (lead.tags ? [lead.tags] : []),
        }));

        const { data: rpcResult, error: rpcError } = await supabase
          .rpc('bulk_insert_leads', {
            leads_data: chunkJson as any,
            workspace_id_param: workspaceId
          });

        if (rpcError) {
          logger.error(`[useBulkCreateLeads] Chunk ${i + 1} RPC error`, rpcError, { chunk: i + 1, workspaceId });
          errors.push({ chunk: i + 1, error: rpcError });
        } else if (rpcResult) {
          const result = rpcResult as any;
          if (result && typeof result === 'object') {
            if (result.success === true || result.success === false) {
              // RPC returned proper result object
              if (result.success) {
                logger.action('bulkImportChunkSuccess', { chunk: i + 1, inserted: result.inserted, workspaceId });
                successCount += result.inserted || 0;
                if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
                  logger.error(`[useBulkCreateLeads] Chunk ${i + 1} had errors`, { errors: result.errors }, { chunk: i + 1, workspaceId });
                  // Add individual row errors to the errors array
                  result.errors.forEach((rowError: any, idx: number) => {
                    errors.push({ 
                      chunk: i + 1, 
                      row: idx + 1,
                      error: { 
                        message: rowError.error || rowError.message || 'Row validation failed',
                        details: rowError.row ? JSON.stringify(rowError.row) : undefined
                      } 
                    });
                  });
                }
              } else {
                // RPC returned success: false
                let errorMsg = result.error || 'Unknown RPC error';
                
                // If there are individual row errors, summarize them
                if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
                  logger.error(`[useBulkCreateLeads] Chunk ${i + 1} has row errors`, { errors: result.errors }, { chunk: i + 1, workspaceId });
                  
                  const errorMessages = result.errors
                    .map((e: any) => {
                      // Extract error message from various possible formats
                      if (typeof e === 'string') return e;
                      if (e.error) return e.error;
                      if (e.message) return e.message;
                      if (e.sqlstate) return `SQL Error (${e.sqlstate})`;
                      return 'Validation error';
                    })
                    .filter((msg: string) => msg && msg !== 'Validation error')
                    .slice(0, 3); // Show first 3 unique errors
                  
                  if (errorMessages.length > 0) {
                    errorMsg = `${result.error_count || result.errors.length} row(s) failed: ${errorMessages.join('; ')}`;
                  } else {
                    // If no specific error messages, try to get SQLSTATE or other info
                    const sqlStates = result.errors
                      .map((e: any) => e.sqlstate)
                      .filter(Boolean)
                      .slice(0, 3);
                    if (sqlStates.length > 0) {
                      errorMsg = `${result.error_count || result.errors.length} row(s) failed with SQL errors: ${sqlStates.join(', ')}`;
                    } else {
                      errorMsg = `${result.error_count || result.errors.length} row(s) failed validation`;
                    }
                  }
                  
                  // Add individual row errors
                  result.errors.forEach((rowError: any, idx: number) => {
                    errors.push({ 
                      chunk: i + 1, 
                      row: idx + 1,
                      error: { 
                        message: rowError.error || rowError.message || 'Row validation failed',
                        details: rowError.row ? JSON.stringify(rowError.row) : undefined,
                        sqlstate: rowError.sqlstate
                      } 
                    });
                  });
                }
                
                logger.error(`[useBulkCreateLeads] Chunk ${i + 1} RPC returned error`, { error: errorMsg, result }, { chunk: i + 1, workspaceId });
                errors.push({ chunk: i + 1, error: { message: errorMsg, details: JSON.stringify(result) } });
              }
            } else {
              // RPC might have returned something unexpected
              logger.error(`[useBulkCreateLeads] Chunk ${i + 1} RPC returned unexpected format`, { result: rpcResult }, { chunk: i + 1, workspaceId });
              errors.push({ chunk: i + 1, error: { message: 'Unexpected RPC response format' } });
            }
          } else {
            logger.error(`[useBulkCreateLeads] Chunk ${i + 1} RPC returned non-object`, { type: typeof rpcResult, result: rpcResult }, { chunk: i + 1, workspaceId });
            errors.push({ chunk: i + 1, error: { message: 'RPC returned invalid response' } });
          }
        } else {
          logger.error(`[useBulkCreateLeads] Chunk ${i + 1} RPC returned null/undefined`, new Error('RPC returned null'), { chunk: i + 1, workspaceId });
          errors.push({ chunk: i + 1, error: { message: 'RPC returned no data' } });
        }
      }

      // Handle partial failures
      if (errors.length > 0) {
        const totalErrors = errors.length;
        const totalChunks = chunks.length;
        const failedCount = leadsToInsert.length - successCount;

        // Extract error messages from RPC responses
        const errorMessages = errors.map(e => {
          if (e.error?.message) {
            return e.error.message;
          }
          if (typeof e.error === 'string') {
            return e.error;
          }
          if (e.error?.details) {
            // Try to parse JSON details to extract meaningful error
            try {
              const details = typeof e.error.details === 'string' ? JSON.parse(e.error.details) : e.error.details;
              if (details.error) return details.error;
              if (details.errors && Array.isArray(details.errors) && details.errors.length > 0) {
                const firstError = details.errors[0];
                return firstError.error || firstError.message || 'Row validation failed';
              }
            } catch {
              // If parsing fails, use the raw details string (but truncate if too long)
              const detailsStr = String(e.error.details);
              return detailsStr.length > 100 ? detailsStr.substring(0, 100) + '...' : detailsStr;
            }
          }
          return 'Unknown error';
        }).filter(Boolean);

        // Remove duplicates and limit to first 3 for readability
        const uniqueErrors = [...new Set(errorMessages)].slice(0, 3);
        const errorDetails = uniqueErrors.length > 0 
          ? uniqueErrors.join('; ') 
          : 'See console for details.';

        const errorMessage = successCount > 0
          ? `Partially imported: ${successCount} of ${leadsToInsert.length} leads. ${errorDetails}`
          : `Failed to import ${failedCount} of ${leadsToInsert.length} leads. ${errorDetails}`;

        logger.error('[useBulkCreateLeads] Import failure', {
          totalLeads: leadsToInsert.length,
          successCount,
          failedCount,
          errors: errors.map(e => ({
            chunk: e.chunk,
            error: e.error
          }))
        });

        toast({
          title: successCount > 0 ? 'Partial import failure' : 'Import failed',
          description: errorMessage,
          variant: 'destructive'
        });

        // If some succeeded, still return results but throw to indicate partial failure
        if (successCount > 0) {
          throw new Error(errorMessage);
        } else {
          throw new Error(errorMessage);
        }
      }

      logger.action('bulkImportSuccess', { successCount, totalLeads: leadsToInsert.length, workspaceId });
      
      return { successCount };
    },
    onSuccess: (data) => {
      // CRITICAL: Invalidate all leads queries (exact: false to match all workspace scopes)
      queryClient.invalidateQueries({ queryKey: ['leads'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['deals'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['elite-today-plan'], exact: false });
      const count = (data as any)?.successCount || 0;
      toast({ 
        title: 'Import complete', 
        description: `${count} lead${count !== 1 ? 's' : ''} imported successfully.` 
      });
    },
    onError: (error) => {
      logger.error('[useBulkCreateLeads] Mutation error', error, { workspaceId: activeWorkspaceId });
      // Error toast is already shown in mutationFn
    },
  });
}

/**
 * MARK DONE - Same as "Mark as complete": sets status='done', completed_at.
 * Does NOT mark lead as lost. Uses completeLead for single source of truth.
 */
export function useMarkLeadDone() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ leadId, outcome }: { leadId: string; outcome?: ActionOutcome }) => {
      if (!activeWorkspaceId) throw new Error('No workspace');
      const scope = { type: 'single' as const, workspaceId: activeWorkspaceId };
      const lead = await LeadsService.getLead(leadId, scope);
      if (!lead) throw new Error('Lead not found');

      await completeLead(leadId, activeWorkspaceId);

      if (activeWorkspaceId && user?.id) {
        try {
          await logActionEvent(activeWorkspaceId, user.id, leadId, 'done', outcome);
        } catch (error: any) {
          if (error?.code === '42P01' || error?.message?.includes('does not exist')) {
            logger.warn('[useMarkLeadDone] action_events table does not exist, skipping log', { leadId, workspaceId: activeWorkspaceId });
          } else {
            throw error;
          }
        }
      }

      return lead;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast({ title: 'Action completed', description: 'Follow-up marked as done. Lead is now inactive.' });
    },
    onError: (error) => {
      handleError(error, { action: 'Mark Lead Done', component: 'useMarkLeadDone', workspaceId: activeWorkspaceId });
    },
  });
}

export function useDeferLead() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ leadId, days = 1 }: { leadId: string; days?: number }) => {
      if (!activeWorkspaceId) throw new Error('No workspace');
      const nextDate = new Date();
      nextDate.setDate(nextDate.getDate() + days);
      nextDate.setHours(9, 0, 0, 0); // Set to 9 AM by default
      
      // Get lead name for event title
      const scope = { type: 'single' as const, workspaceId: activeWorkspaceId };
      const lead = await LeadsService.getLead(leadId, scope);
      const leadName = lead?.name || 'Lead';
      
      // Update lead with next_follow_up_at (decision-first model)
      const updated = await LeadsService.updateLead({
        id: leadId,
        workspace_id: activeWorkspaceId,
        next_follow_up_at: nextDate.toISOString(), // Use next_follow_up_at, not next_action_at
        // follow_up_status will be set automatically by trigger
      });

      // Create calendar event for the follow-up
      if (activeWorkspaceId && user?.id) {
        try {
          const { EventsService } = await import('@/data/events.service');
          const endDate = new Date(nextDate);
          endDate.setHours(nextDate.getHours() + 1); // 1 hour duration
          
          const createdEvent = await EventsService.createEvent({
            workspace_id: activeWorkspaceId,
            title: `Follow up: ${leadName}`,
            type: 'meeting',
            start_at: nextDate.toISOString(),
            end_at: endDate.toISOString(),
            lead_id: leadId,
            priority: 'normal',
            reminder_offsets_minutes: [60, 15, 0], // 1 hour, 15 min, and at time
          });
          
          logger.info('[useDeferLead] Calendar event created', { 
            eventId: createdEvent.id, 
            leadId, 
            startAt: nextDate.toISOString(),
            workspaceId: activeWorkspaceId 
          });
        } catch (error: any) {
          // Log detailed error but don't fail the whole operation
          logger.error('[useDeferLead] Failed to create calendar event', error, { 
            leadId, 
            workspaceId: activeWorkspaceId,
            errorCode: error?.code,
            errorMessage: error?.message,
            errorDetails: error?.details
          });
          // Show user-friendly error but don't block the defer operation
          toast({ 
            title: 'Follow-up deferred', 
            description: 'Lead updated, but calendar event creation failed. Please check console for details.',
            variant: 'default'
          });
        }
      }

      // Log the action event
      if (activeWorkspaceId && user?.id) {
        await logActionEvent(activeWorkspaceId, user.id, leadId, 'defer');
      }

      return updated;
    },
    onSuccess: (_, { days = 1 }) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      queryClient.invalidateQueries({ queryKey: ['reminder-events'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast({ title: 'Deferred', description: `Follow-up moved by ${days} day(s).` });
    },
    onError: (error) => {
      handleError(error, { action: 'Defer Lead', component: 'useDeferLead', workspaceId: activeWorkspaceId });
    },
  });
}

export function useLogWhatsAppAction() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (leadId: string) => {
      if (!activeWorkspaceId || !user?.id) throw new Error('No workspace or user');
      await logActionEvent(activeWorkspaceId, user.id, leadId, 'whatsapp');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });
}

export function useLogCallAction() {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (leadId: string) => {
      if (!activeWorkspaceId || !user?.id) throw new Error('No workspace or user');
      await logActionEvent(activeWorkspaceId, user.id, leadId, 'call');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });
}
